import * as React from 'react';
import type { CalendarEvent } from '../../../MedicareProAPI/Entities/CalendarEvent';
import type { Ind } from '../../../MedicareProAPI/Entities/Ind/Ind';
import {
  MakeCalendarDeliveryMethod,
  MakeCalendarType,
  Utilities,
} from '../../../MedicareProAPI/Utilities/Utilities';
import type { SelectMap } from '../../Utilities/SelectMap';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import { IndRepository } from '../../../MedicareProAPI/Repositories/Ind/IndRepository';
import { UserContext } from '../../Context/UserContext';
import { IndSearch } from '../Form/Field/IndSearch';
import { UserRepository } from '../../../MedicareProAPI/Repositories/User/UserRepository';
import { ProviderCalendarEvent } from '../../../MedicareProAPI/Entities/External/ProviderCalendarEvent';
import RichTextEditor from '../Form/Field/RichTextEditor';
import { HTMLPreview } from '../HTMLPreview';
import { RichTextEditorLight } from '../Form/Field/RichTextEditorLight';
import { IndAddress } from '../../../MedicareProAPI/Entities/Ind/IndAddress';
import { UserCalendarProviderType } from '../../../MedicareProAPI/Entities/User/UserCalendarProvider';
import { UserOffice } from '../../../MedicareProAPI/Entities/User/UserOffice';
import { UserOfficeRepository } from '../../../MedicareProAPI/Repositories/User/UserOfficeRepository';

export interface CalendarEventFormProps {
  calendarEvent?: CalendarEvent;
  users?: User[];
  isEditable?: boolean;
  onSave: (calendarEvent: CalendarEvent) => Promise<CalendarEvent>;
  onDelete?: (calendarEvent: CalendarEvent) => Promise<void>;
}

const userCalendarProviderTypeList: Array<SelectMap> = [
  {
    value: UserCalendarProviderType.GMAIL,
    label: UserCalendarProviderType.GMAIL,
  },
  {
    value: UserCalendarProviderType.OUTLOOK,
    label: UserCalendarProviderType.OUTLOOK,
  },
];

type AddressOption = {
  value: string;
  label: string;
};

export const CalendarEventForm = (props: CalendarEventFormProps): React.JSX.Element => {
  const [formError, setFormError] = React.useState<string>();
  const [formLoading, setFormLoading] = React.useState<boolean>(false);
  const [formDeleting, setFormDeleting] = React.useState<boolean>(false);
  const [formId, setFormId] = React.useState<number>();
  const [formName, setFormName] = React.useState<string>();
  const [formInds, setFormInds] = React.useState<Array<Ind>>();
  const [formUser, setFormUser] = React.useState<User>();
  const [formDescription, setFormDescription] = React.useState<string>();
  const [formLocationOptions, setFormLocationOptions] = React.useState<Array<any>>();
  const [formLocation, setFormLocation] = React.useState<string>();
  const [formDateTimeStart, setFormDateTimeStart] = React.useState<Date>();
  const [formDateTimeEnd, setFormDateTimeEnd] = React.useState<Date>();
  const [formIsAllDayEvent, setFormIsAllDayEvent] = React.useState<boolean>();
  const [formExternalID, setFormExternalID] = React.useState<string>();
  const [formExternalLink, setFormExternalLink] = React.useState<string>();
  const [calendarEvent, setCalendarEvent] = React.useState<CalendarEvent>();
  const [timeIncrements, setTimeIncrements] = React.useState<SelectMap[]>();

  const [isEditable, setIsEditable] = React.useState<boolean>();

  // Create ICS/Google Calendar link?
  const [formCalendarProviderLink, setFormCalendarProviderLink] = React.useState<string>(
    localStorage.getItem('calender-external-selection') || null
  );

  const [users, setUsers] = React.useState<User[]>();
  const [individuals, setInds] = React.useState<Ind[]>();

  const [userAddress, setUserAddress] = React.useState<Array<AddressOption>>([]);
  const [userOffices, setUserOffices] = React.useState<Array<AddressOption>>([]);
  const [formUserAddress, setFormUserAddress] = React.useState<Array<AddressOption>>([]);
  const [formIndAddresses, setFormIndAddresses] = React.useState<Array<AddressOption>>([]);

  const userData = React.useContext(UserContext);

  const individualRepository = new IndRepository();
  const userRepository = new UserRepository();
  const userOfficeRepository = new UserOfficeRepository();

  if (userData) {
    individualRepository.setAccessToken(userData.userAccessToken);
    userRepository.setAccessToken(userData.userAccessToken);
    userOfficeRepository.setAccessToken(userData.userAccessToken);
  }

  const assembleUserAddress = (user: User) => {
    const userAdddressParts = [];
    const userAdddressParts2 = [];
    const finalAddress = [];

    if (user.getUserAddressStreet()) {
      userAdddressParts.push(user.getUserAddressStreet());
    }

    if (user.getUserAddressUnit()) {
      userAdddressParts.push(user.getUserAddressUnit());
    }

    if (user.getUserAddressCity()) {
      userAdddressParts.push(user.getUserAddressCity());
    }

    if (user.getUserAddressState()) {
      userAdddressParts2.push(user.getUserAddressState());
    }

    if (user.getUserAddressZip()) {
      userAdddressParts2.push(user.getUserAddressZip());
    }

    if (userAdddressParts.length > 0) {
      const value = userAdddressParts.join(' ') + ', ' + userAdddressParts2.join(' ');
      const obj = { value: value, label: 'Office - ' + value };
      finalAddress.push(obj);
    }

    return finalAddress;
  };

  const assembleUserOffices = (userOffices) => {
    const finalAddress = [];

    if (userOffices.length > 0) {
      userOffices.forEach((office) => {
        const userAdddressParts = [];
        const userAdddressParts2 = [];

        if (office.getOfficeAddress1()) {
          userAdddressParts.push(office.getOfficeAddress1());
        }

        if (office.getOfficeAddress2()) {
          userAdddressParts.push(office.getOfficeAddress2());
        }

        if (office.getOfficeCity()) {
          userAdddressParts.push(office.getOfficeCity());
        }

        if (office.getOfficeState()) {
          userAdddressParts2.push(office.getOfficeState());
        }

        if (office.getOfficeZip()) {
          userAdddressParts2.push(office.getOfficeZip());
        }

        if (userAdddressParts.length > 0) {
          const value = userAdddressParts.join(' ') + ', ' + userAdddressParts2.join(' ');
          const obj = {
            value: value,
            label: office.getOfficeName() + ' - ' + value,
          };
          finalAddress.push(obj);
        }
      });
    }
    return finalAddress;
  };

  const fetchFormUsersAddresses = (user) => {
    userOfficeRepository.getUserOffices(user.getUserId()).then((response) => {
      const offices = response?.getItems?.();
      if (offices && offices.length > 0) {
        // Use offices only if they exist, otherwise use the users address on file
        setUserOffices(assembleUserOffices(offices));
      } else {
        const address = assembleUserAddress(user);
        if (address.length > 0) {
          setUserAddress(address);
        }
      }
    });
  };

  /**
   * Get currently signed in user's address
   */
  /* React.useEffect(() => {
       fetchFormUsersAddresses(userData)
      }, []); */

  const autoDefaultedRef = React.useRef(false);

  React.useEffect(() => {
    const all = [...(formIndAddresses ?? []), ...(userAddress ?? []), ...(userOffices ?? [])].map(
      ({ value, label }) => ({ value, label })
    );

    // de-dupe
    const seen = new Set<string>();
    let opts = all.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));

    // ensure current selection is visible even if its option hasn't loaded yet
    if (formLocation && !opts.some((o) => o.value === formLocation)) {
      opts = [{ value: formLocation, label: 'Current location' }, ...opts];
    }

    setFormLocationOptions(opts);

    // don’t override an existing non-empty selection
    setFormLocation((prev) => {
      if (prev) return prev;
      if (autoDefaultedRef.current) return prev;
      autoDefaultedRef.current = true;
      return opts[0]?.value ?? '';
    });
  }, [formIndAddresses, userAddress, userOffices]);

  /**
   * Handles the CalendarEvent being updated
   */
  React.useEffect(() => {
    if (props.calendarEvent) {
      if (!props.calendarEvent.getDateTimeStart()) {
        const newStartDate = new Date();
        newStartDate.setMinutes(
          newStartDate.getMinutes() >= 45 || newStartDate.getMinutes() <= 15 ? 0 : 30
        );
        newStartDate.setSeconds(0);

        props.calendarEvent.setDateTimeStart(newStartDate);
      }

      setFormId(props.calendarEvent.getId());
      setFormName(props.calendarEvent.getName());
      setFormInds(props.calendarEvent.getInds());
      setFormUser(props.calendarEvent.getUser());
      setFormDescription(props.calendarEvent.getDescription());
      setFormLocation(props.calendarEvent.getLocation());

      if (props.calendarEvent instanceof ProviderCalendarEvent) {
        setFormExternalID(props.calendarEvent.getExternalID());
        setFormExternalLink(props.calendarEvent.getExternalLink());
      }

      // TODO: You need to define the end before the start due to sequencing - we should change this.
      handleFormDateTimeEnd(props.calendarEvent.getDateTimeEnd());
      handleFormDateTimeStart(props.calendarEvent.getDateTimeStart());
      setFormIsAllDayEvent(props.calendarEvent.isAllDayEvent() || false);
    } else {
      setFormId(undefined);
      setFormName(undefined);
      setFormInds(undefined);
      setFormUser(undefined);
      setFormDescription(undefined);
      setFormLocation(undefined);
      setFormExternalID(undefined);
      setFormExternalLink(undefined);
      setFormDateTimeEnd(undefined);
      setFormDateTimeStart(undefined);
      setFormIsAllDayEvent(false);
    }
  }, [calendarEvent]);

  /**
   * Handles props.calendarEvent changing
   */
  React.useEffect(() => {
    setIsEditable(props.isEditable === false ? false : true);
  }, [props.isEditable]);

  /**
   * Handles props.calendarEvent changing
   */
  React.useEffect(() => {
    setCalendarEvent(props.calendarEvent);
  }, [props.calendarEvent]);

  /**
   * Handles updating the user list via props.users
   */
  React.useEffect(() => {
    setUsers(props.users);

    if (props.users && props.users.length > 0) {
      setFormUser(props.users[0]);
    } else {
      // should get the user object
      userRepository.getUser(userData.userId).then((response) => {
        setFormUser(response);
      });
    }
    console.log('handling props.users');
  }, [props.users]);

  /**
   * Handles updating the current user and updating their individuals
   */
  React.useEffect(() => {
    if (formUser) {
      // Set form user address
      /* let newFormUserAddress: string = null;

            if( formUser.getUserId() !== userData.userId ){

                const userAddress = assembleUserAddress(formUser);
            
                if (userAddress !== '') {
                    setFormUserAddress(userAddress);
                }

            } */
      fetchFormUsersAddresses(formUser);

      // Get form
      individualRepository
        .getInds({
          limit: 100,
          include: ['addresses', 'spouse', 'spouse.addresses', 'phones', 'spouse.phones'],
          criteria: { ownerId: formUser.getUserId() },
        })
        .then((individualsResponse) => {
          setInds(individualsResponse.getItems());
        });
    }
  }, [formUser]);

  /**
   * Each time an ind changes re-compile the list of addresses
   */
  React.useEffect(() => {
    // Create new address list
    const newAddresses = [];

    // If this event is assigned to one or more INDs then pull their addresses
    if (formInds) {
      formInds.forEach((ind) => {
        if (ind.getAddresses() && ind.getAddresses().getItems()) {
          ind
            .getAddresses()
            .getItems()
            .forEach((address) => {
              const addressParts = [];
              const addressParts2 = [];

              if (address.getIndAddressStreet()) {
                addressParts.push(address.getIndAddressStreet());
              }

              if (address.getIndAddressUnit()) {
                addressParts.push(address.getIndAddressUnit());
              }

              if (address.getIndAddressCity()) {
                addressParts.push(address.getIndAddressCity());
              }

              if (address.getIndAddressState()) {
                addressParts2.push(address.getIndAddressState());
              }

              if (address.getIndAddressZip()) {
                addressParts2.push(address.getIndAddressZip());
              }

              if (addressParts.length > 0) {
                const value = addressParts.join(' ') + ', ' + addressParts2.join(' ');
                const obj = {
                  value: value,
                  label: address.getIndAddressType() + ' - ' + value,
                };
                newAddresses.push(obj);
              }
            });
        }
      });

      if (formInds.length === 1) {
        setFormName(formInds[0].getIndName(1) + ' (appointment)');
      }
      if (formInds.length === 2) {
        setFormName(formInds[0].getIndJointSpouseName(1) + ' (appointment)');
      }
    }

    setFormIndAddresses(newAddresses);
  }, [formInds]);

  /**
   * If the date is updated, then parse the string and update/create Date obhect
   *
   * @param {string} dateString Newly provided date string we want to parse
   * @param {Date} existingDate (Optional) Existing date we want to modify, if needed
   * @returns {Date} Parsed date
   */
  const parseDateString = (dateString: string, existingDate?: Date): Date => {
    const dateStringParts = dateString.split('-');

    const dateStringYear = parseInt(dateStringParts[0]);
    const dateStringMonth = parseInt(dateStringParts[1]) - 1;
    const dateStringDay = parseInt(dateStringParts[2]);

    const newDate = new Date(existingDate || undefined);
    newDate.setFullYear(dateStringYear);
    newDate.setMonth(dateStringMonth);
    newDate.setDate(dateStringDay);

    // If this is a new date then set the minutes and seconds to 0 to round the time nicely
    if (!existingDate) {
      newDate.setMinutes(0);
      newDate.setSeconds(0);
    }

    return newDate;
  };

  /**
   * Handles the input parsing from the Form Date Tiem End field
   *
   * @param {number} dateTime New date time
   */
  const handleFormDateTimeEndParse = (dateTime: number): void => {
    const parsedDate = new Date();
    parsedDate.setTime(dateTime);

    handleFormDateTimeEnd(parsedDate);
  };

  /**
   * Update date time end
   *
   * @param {Date} date New date time end
   */
  const handleFormDateTimeEnd = (date: Date): void => {
    setFormDateTimeEnd(date);
  };

  /**
   * Handles the start date field being changed
   *
   * @param {string} dateTimeString String to parse, in the format YYYY-MM-DDTHH:II::SS
   */
  const handleFormDateTimeStartParse = (dateString: string) => {
    const parsedDate = Utilities.dateTimeStringToDateTime(dateString, false);
    handleFormDateTimeStart(parsedDate);
  };

  /**
   * Handles the start date field being changed
   *
   * @param {string} dateTimeString String to parse, in the format YYYY-MM-DDTHH:II::SS
   */
  const handleFormDateTimeStart = (date: Date) => {
    if (formDateTimeStart) {
      let newFormDateTimeEnd;

      if (formDateTimeEnd) {
        newFormDateTimeEnd = new Date(formDateTimeEnd);
        const newTime =
          newFormDateTimeEnd.getTime() + (date.getTime() - formDateTimeStart.getTime());
        newFormDateTimeEnd.setTime(newTime);
      } else {
        newFormDateTimeEnd = new Date(date);
        newFormDateTimeEnd.setMinutes(newFormDateTimeEnd.getMinutes() + 30);
      }

      setFormDateTimeEnd(newFormDateTimeEnd);
    }

    setFormDateTimeStart(date);

    // Create time increments
    const timeIncrements: SelectMap[] = [];
    const timeRangeHours = 6;
    const minuteIncrements = 15;
    let timeRangeMinutes = 60 * timeRangeHours;
    let timeRangeCursor = 0;

    while (timeRangeMinutes > timeRangeCursor) {
      timeRangeCursor = timeRangeCursor + minuteIncrements;

      const hoursAndMinutes = Utilities.minutesToHoursAndMinutes(timeRangeCursor);

      const tempDate = new Date(date);
      tempDate.setTime(tempDate.getTime() + timeRangeCursor * 60 * 1000);

      const hoursMinutesString = (
        (hoursAndMinutes.hours ? hoursAndMinutes.hours + ' hours' : '') +
        ' ' +
        (hoursAndMinutes.minutes ? hoursAndMinutes.minutes + ' minutes' : '')
      ).trim();
      const timeString = tempDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const newIncrement: SelectMap = {
        label: timeString + ' (' + hoursMinutesString + ')',
        value: tempDate.getTime(),
      };

      timeIncrements.push(newIncrement);

      timeRangeMinutes = timeRangeMinutes - minuteIncrements;
    }

    setTimeIncrements(timeIncrements);
  };

  /**
   * Handles the start date field being changed
   *
   * @param {string} dateString String to parse, in the format YYYY-MM-DD
   */
  const handleFormDateStart = (dateString: string) => {
    const parsedDate = parseDateString(dateString, formDateTimeStart);

    setFormDateTimeStart(parsedDate);
  };

  /**
   * Handle changing whether or not an external calendar link should be created
   *
   * @param {User} user New user value
   */
  const handleChangeCalendarProviderLink = (providerName: string) => {
    const newProviderName = providerName ? providerName : null;

    setFormCalendarProviderLink(newProviderName);
  };

  /**
   * Handle changing the selected user
   *
   * @param {User} user New user value
   */
  const handleChangeUser = (user: User) => {
    setFormUser(user);
    setFormInds(null);
  };

  /**
   * Handles the deleting of a CalendarEvent
   *
   * @param {CalendarEvent} calendarEvent CalendarEvent to delete
   * @returns {Promise<void|Error>} Promise resolving on the CalendarEvent being deleted
   */
  const handleDelete = (calendarEvent: CalendarEvent): Promise<void | Error> => {
    setFormDeleting(true);

    return props.onDelete(calendarEvent);
  };

  /**
   * Handles the craetion or updating of a CalendarEvent
   *
   * @param {CalendarEvent} calendarEvent CalendarEvent to update or create
   * @returns {Promise<CalendarEvent|Error>} Promise resolving on the CalendarEvent being update/created
   */
  const handleSave = (calendarEvent: CalendarEvent): Promise<CalendarEvent | Error> => {
    setFormLoading(true);
    setFormError(null);

    calendarEvent
      .setName(formName)
      .setInds(formInds)
      .setUser(formUser)
      .setLocation(formLocation)
      .setDateTimeStart(formDateTimeStart)
      .setDateTimeEnd(formDateTimeEnd)
      .setDescription(formDescription)
      .setIsAllDayEvent(formIsAllDayEvent);

    return props
      .onSave(calendarEvent)
      .then((updatedCalendarEvent) => {
        // Only trigger calendar event creation if the user is creating an event for themselves
        if (formCalendarProviderLink) {
          // Gmail is handled differently to other providers
          const isGmail = formCalendarProviderLink === UserCalendarProviderType.GMAIL;

          let description = updatedCalendarEvent.getDescription() || '';
          const formIndsIds = formInds.map((item) => item.getIndId());

          if (formInds) {
            const indsProcessed = [];
            formInds.forEach((indSingle) => {
              if (!indsProcessed.includes(indSingle.getIndId())) {
                description += '\n---------\n' + 'Name: ' + indSingle.getIndName();

                if (indSingle.getIndDob()) {
                  description += '\nDOB: ' + indSingle.getIndDobFormatted();
                }

                if (indSingle.getDefaultPhone()) {
                  description += '\nPhone: ' + indSingle.getDefaultPhone().getIndPhoneNumber();
                }

                if (indSingle.getDefaultAddress()) {
                  description += '\nZip: ' + indSingle.getDefaultAddress().getIndAddressZip();
                }

                isGmail
                  ? (description +=
                      '\n<a href="https://medicareproapp.com/agent/profile/index/' +
                      indSingle.getIndId() +
                      '">Profile Link</a>')
                  : (description +=
                      '\nProfile URL: https://medicareproapp.com/agent/profile/index/' +
                      indSingle.getIndId());
                description + '\n';
                indsProcessed.push(indSingle.getIndId());
                if (
                  indSingle.getSpouse() &&
                  !indsProcessed.includes(indSingle.getSpouse().getIndId()) &&
                  formIndsIds.includes(indSingle.getSpouse().getIndId())
                ) {
                  const spouse = indSingle.getSpouse();
                  description += '\n---------\n' + 'Name: ' + spouse.getIndName();

                  if (spouse.getIndDob()) {
                    description += '\nDOB: ' + spouse.getIndDobFormatted();
                  }

                  if (spouse.getDefaultPhone()) {
                    description += '\nPhone: ' + spouse.getDefaultPhone().getIndPhoneNumber();
                  }

                  if (spouse.getDefaultAddress()) {
                    description += '\nZip: ' + spouse.getDefaultAddress().getIndAddressZip();
                  }

                  isGmail
                    ? (description +=
                        '\n<a href="https://medicareproapp.com/agent/profileController/index/' +
                        spouse.getIndId() +
                        '">Profile Link</a>')
                    : (description +=
                        '\nProfile URL: https://medicareproapp.com/agent/profileController/index/' +
                        spouse.getIndId());
                  description + '\n\n';
                  indsProcessed.push(indSingle.getSpouse().getIndId());
                }
              }
            });
          }

          const htmlDescription = description.replace(/\n/g, '<br />');

          // Handle creating Gmail/ICS
          Utilities.makeEvent(
            updatedCalendarEvent.getName(), //+' '+nameAppendText,
            isGmail ? htmlDescription : description,
            updatedCalendarEvent.getLocation(),
            updatedCalendarEvent.getDateTimeStart(),
            updatedCalendarEvent.getDateTimeEnd(),
            isGmail ? MakeCalendarType.gmail : MakeCalendarType.ics,
            MakeCalendarDeliveryMethod.redirect
          );

          setFormCalendarProviderLink(null);
        }

        setCalendarEvent(updatedCalendarEvent);

        return calendarEvent;
      })
      .catch((e: Error) => {
        setFormError('Please ensure all fields have been completed');
        return e;
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  return (
    <div className="row">
      {formError && (
        <div className="col-12">
          <div className="small alert alert-danger p-3">{formError}</div>
        </div>
      )}

      <div className="mb-2 col-12">
        <label className="label">Event Title</label>
        <div className="input-group">
          {isEditable === true && (
            <input
              disabled={formLoading}
              type="text"
              className="form-control border-secondary"
              autoComplete="off"
              value={formName || ''}
              onChange={(e) => setFormName(e.target.value)}
            />
          )}
          {isEditable === false && <p>{formName}</p>}
        </div>
      </div>

      <div className="mb-2 col-12">
        <label className="label">Date & Time</label>
        {isEditable === false && (
          <>
            <div className="row">
              <div className="col-12">
                {formIsAllDayEvent === true && (
                  <p className="mt-1">
                    All day event on{' '}
                    {formDateTimeStart ? Utilities.renderDateLocal(formDateTimeStart) : ''}
                  </p>
                )}
                {formIsAllDayEvent === false && (
                  <p className="mt-1">
                    {formDateTimeStart &&
                      formDateTimeEnd &&
                      formDateTimeStart.toDateString() === formDateTimeEnd.toDateString() && (
                        <>
                          {formDateTimeStart.toLocaleDateString()}{' '}
                          {formDateTimeStart.toLocaleTimeString()} -{' '}
                          {formDateTimeEnd.toLocaleTimeString()}
                        </>
                      )}
                    {formDateTimeStart &&
                      formDateTimeEnd &&
                      formDateTimeStart.toDateString() !== formDateTimeEnd.toDateString() && (
                        <>
                          {formDateTimeStart.toLocaleString()} - {formDateTimeEnd.toLocaleString()}
                        </>
                      )}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
        {isEditable === true && (
          <>
            <div className="row">
              <div className="col-6">
                <div className="input-group">
                  {formIsAllDayEvent === true && (
                    <input
                      disabled={formLoading}
                      type="date"
                      className="form-control border-secondary"
                      autoComplete="off"
                      value={
                        formDateTimeStart ? Utilities.renderDateLocal(formDateTimeStart) : undefined
                      }
                      onChange={(e) => handleFormDateStart(e.target.value)}
                      onKeyUp={(e) => handleFormDateStart(e.currentTarget.value)}
                    />
                  )}
                  {formIsAllDayEvent === false && (
                    <input
                      disabled={formLoading}
                      type="datetime-local"
                      className="form-control border-secondary"
                      autoComplete="off"
                      value={
                        formDateTimeStart
                          ? Utilities.renderDateTimeLocal(formDateTimeStart)
                          : undefined
                      }
                      onChange={(e) => handleFormDateTimeStartParse(e.target.value)}
                      onKeyUp={(e) => handleFormDateTimeStartParse(e.currentTarget.value)}
                    />
                  )}
                </div>
              </div>
              <div className="col-6">
                {formIsAllDayEvent === false && formDateTimeStart && (
                  <select
                    value={formDateTimeEnd ? formDateTimeEnd.getTime() : undefined}
                    onChange={(e) => handleFormDateTimeEndParse(parseInt(e.target.value))}
                    className="form-control border-secondary"
                  >
                    {timeIncrements &&
                      timeIncrements.map((increment) => {
                        return (
                          <option key={`timeincrement-${increment.value}`} value={increment.value}>
                            {increment.label}
                          </option>
                        );
                      })}
                  </select>
                )}
              </div>
            </div>

            <div className="clearfix mt-2">
              <label className="switch float-left mr-1">
                <input
                  type="checkbox"
                  disabled={formLoading || !isEditable}
                  data-status="off"
                  checked={formIsAllDayEvent === true}
                  onChange={(e) => {
                    setFormIsAllDayEvent(!formIsAllDayEvent);
                  }}
                />
                <span className="slider round pt-1"></span>
              </label>

              <div className="small pt-0">All day event?</div>
            </div>
          </>
        )}
      </div>
      {!(calendarEvent instanceof ProviderCalendarEvent) &&
        isEditable === true &&
        users &&
        users.length > 0 && (
          <div className="mb-2 col-12">
            <label className="label">Agent</label>

            {
              <select
                onChange={(e) => {
                  const selectedUser = users.find(
                    (u) => u.getUserId().toString() === e.target.value
                  );
                  if (selectedUser) handleChangeUser(selectedUser);
                }}
                className="form-control"
                disabled={formLoading || !isEditable}
                value={formUser ? formUser.getUserId() : ''}
              >
                {users.map((userSingle) => (
                  <option value={userSingle.getUserId()} key={`user-${userSingle.getUserId()}`}>
                    {userSingle.getUserName()} ({userSingle.getUserUsername()})
                  </option>
                ))}
              </select>
            }
          </div>
        )}

      {isEditable === true && (
        <div className="mb-2 col-12">
          <label className="label">Client</label>
          <IndSearch
            onChange={(individual) => setFormInds(individual ? [individual] : null)}
            include={['addresses', 'spouse', 'spouse.addresses']}
            value={formInds && formInds.length > 0 ? formInds[0] : null}
          />

          {formInds && formInds[0] && formInds[0].getSpouse() && (
            <div className="clearfix mt-2">
              <label className="switch float-left mr-1">
                <input
                  type="checkbox"
                  disabled={formLoading || !isEditable}
                  data-status="off"
                  checked={formInds.length === 2}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newFormInds = [...formInds];

                    if (checked) {
                      newFormInds.push(formInds[0].getSpouse());
                    } else {
                      newFormInds.splice(1, 1);
                    }

                    setFormInds(newFormInds);
                  }}
                />
                <span className="slider round pt-1"></span>
              </label>

              <div className="small pt-0">
                Include spouse ({formInds[0].getSpouse().getIndName()})
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-2 col-12">
        {(!(calendarEvent instanceof ProviderCalendarEvent) ||
          (calendarEvent instanceof ProviderCalendarEvent && !formExternalID)) &&
          isEditable === true && (
            <>
              <label className="label">Location</label>

              <select
                onChange={(e) => setFormLocation(e.target.value ? e.target.value : undefined)}
                className="form-control border-secondary"
                disabled={formLoading || !isEditable}
                value={formLocation || ''}
              >
                {formLocationOptions &&
                  formLocationOptions.map((locationSingle) => (
                    <option key={locationSingle.value} value={locationSingle.value}>
                      {locationSingle.label}
                    </option>
                  ))}
                <option value="Phone/Web">Phone/Web</option>
                <option value="">Other</option>
              </select>

              {!formLocationOptions.some((option) => option.value === formLocation) &&
                formLocation !== 'Phone/Web' && (
                  <div className="input-group mt-2">
                    <input
                      placeholder="Event Location"
                      disabled={formLoading || !isEditable}
                      type="text"
                      className="form-control border-secondary"
                      autoComplete="off"
                      value={formLocation || ''}
                      onChange={(e) => setFormLocation(e.target.value)}
                    />
                  </div>
                )}
            </>
          )}
        {((calendarEvent instanceof ProviderCalendarEvent && formExternalID) ||
          isEditable === false) && (
          <>
            <label className="label">Location</label>
            <p className="mt-2 small mb-0">{formLocation || 'No location'}</p>
          </>
        )}
      </div>

      <div className="mb-2 col-12">
        <label className="label">Notes</label>
        {(!(calendarEvent instanceof ProviderCalendarEvent) ||
          (calendarEvent instanceof ProviderCalendarEvent && !formExternalID)) &&
          isEditable === true && (
            <div className="input-group">
              <RichTextEditorLight
                value={formDescription}
                disabled={formLoading || !isEditable}
                onChange={(value) => {
                  setFormDescription(value);
                }}
              />
            </div>
          )}
        {((calendarEvent instanceof ProviderCalendarEvent && formExternalID) ||
          isEditable === false) && (
          <>
            <HTMLPreview html={calendarEvent.getDescription()} />

            {calendarEvent instanceof ProviderCalendarEvent && formExternalID && (
              <p className="small mt-1">
                Your event description contains HTML and cannot be edited here,{' '}
                <a href={formExternalLink} target="_blank" rel="noreferrer">
                  click here
                </a>{' '}
                to make changes.
              </p>
            )}
          </>
        )}
      </div>
      {!(calendarEvent instanceof ProviderCalendarEvent) && isEditable === true && (
        <div className="mb-2 col-12">
          <label className="label">Also create an event on an external calendar?</label>
          {
            <select
              onChange={(e) => {
                handleChangeCalendarProviderLink(e.target.value);
                localStorage.setItem('calender-external-selection', e.target.value);
              }}
              className="form-control"
              disabled={formLoading || !isEditable}
              value={formCalendarProviderLink || undefined}
            >
              <option value=""></option>
              {userCalendarProviderTypeList.map((calendarProviderSingle) => {
                return (
                  <option key={calendarProviderSingle.value} value={calendarProviderSingle.value}>
                    {calendarProviderSingle.label}
                  </option>
                );
              })}
            </select>
          }
        </div>
      )}

      <div className="col-12">
        {formId && (
          <button
            onClick={(e) => handleDelete(calendarEvent)}
            className="btn btn-danger float-right"
            type="button"
          >
            {formDeleting ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Deleting
              </>
            ) : (
              <>
                <i className="fas fa-trash" /> Delete Event
              </>
            )}
          </button>
        )}

        <button
          onClick={(e) => handleSave(calendarEvent)}
          className="btn btn-success"
          type="button"
          disabled={!isEditable}
          title={isEditable ? 'Click to save your changes' : "This event can't be edited here"}
        >
          {formLoading ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Saving
            </>
          ) : (
            <>
              <i className="fas fa-save" /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};
